import type Widget from "@/model/widget";
import WidgetCard from "./widget-card";
import { Button } from "@/components/ui/button";
import { Loader2, Star } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { useRating } from "@/hooks/use-rating";
import { getItem } from "@/lib/storage-utils";
import { STORAGE, STORAGE_KEY, USER_MODE } from "@/constants";
import type { AppInteraction } from "@/model/browser-storage";
import { lowerCaseComparison } from "@/lib/string-util";

type FeedbackWidgetProps = {
  widget: Widget;
};

const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({ widget }) => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState(""); // Add state for comment
  const authData = getItem(STORAGE_KEY.AUTH_DATA, STORAGE.SESSION);
  const currentRating = rating || hover;
  const interaction = getItem(
    STORAGE_KEY.APP_INTERACTION,
    STORAGE.SESSION,
  ) as AppInteraction;
  const ratingMutation = useRating();
  const isGuestMode: boolean = lowerCaseComparison(
    authData?.mode,
    USER_MODE.GUEST,
  );

  function onFeedbackSubmit(e: React.FormEvent<HTMLFormElement>) {
    if (isGuestMode) return;
    e.preventDefault();
    ratingMutation.mutate({
      ratingCount: rating,
      comment,
    });
  }

  return !interaction?.isRated ? (
    <WidgetCard title={widget.widgetDisplayName}>
      <form
        className="flex h-full flex-col items-center justify-center gap-4 text-[0.875rem]"
        onSubmit={onFeedbackSubmit}
      >
        <p>Please provide us with any feedback to help us serve you better.</p>

        {/*
          2.1.1 Keyboard — A / 4.1.2 Name, Role, Value — A / 1.3.1 — A.
          The rating was five bare Lucide <Star> SVGs with onClick handlers: not focusable,
          no role, no names, no selected state — so the entire rating control did not exist
          for keyboard, switch or screen reader users. The wrapping <div> also carried an
          aria-label with no role, which assistive tech simply drops.

          Native radios inside a fieldset give the radiogroup role, arrow-key navigation,
          checked state and the group's name for free. The inputs are visually hidden but
          NOT display:none, so they remain focusable and announceable; the star graphic is
          decorative and the ring is driven off peer-focus-visible so the focus indicator
          stays visible (2.4.7 Focus Visible — AA).
        */}
        <fieldset className="border-0 p-0" disabled={isGuestMode}>
          <legend className="sr-only">
            Rate your experience from 1 to 5 stars
          </legend>
          <div className="flex gap-0.5">
            {Array.from({ length: 5 }).map((_, index) => {
              const starNumber = index + 1;
              return (
                <label
                  key={index}
                  className="cursor-pointer"
                  onMouseEnter={() => {
                    if (!isGuestMode) setHover(starNumber);
                  }}
                  onMouseLeave={() => {
                    if (!isGuestMode) setHover(0);
                  }}
                >
                  <input
                    type="radio"
                    name="experience-rating"
                    value={starNumber}
                    checked={rating === starNumber}
                    disabled={isGuestMode}
                    onChange={() => setRating(starNumber)}
                    // 2.1.1 Keyboard — A: focus must also preview the rating, or keyboard
                    // users get no equivalent of the mouse hover feedback.
                    onFocus={() => {
                      if (!isGuestMode) setHover(starNumber);
                    }}
                    onBlur={() => {
                      if (!isGuestMode) setHover(0);
                    }}
                    className="peer sr-only"
                  />
                  <span className="sr-only">
                    {starNumber} {starNumber === 1 ? "star" : "stars"}
                  </span>
                  <Star
                    aria-hidden="true"
                    className={`rounded border-none peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 ${!isGuestMode ? "hover:scale-125" : ""} ${starNumber <= currentRating ? "" : "opacity-40"}`}
                    color="none"
                    fill={
                      starNumber <= currentRating || currentRating === 0
                        ? "orange"
                        : "gray"
                    }
                  />
                </label>
              );
            })}
          </div>
        </fieldset>

        {/* 3.3.2 Labels or Instructions — A: the field had only a placeholder, which
            disappears on the first keystroke and is skipped by some screen readers. */}
        <Textarea
          className="h-36 resize-none"
          aria-label="Feedback or comment"
          placeholder="Please add any feedback or comment here if you wish to"
          value={comment}
          disabled={isGuestMode}
          onChange={(e) => setComment(e.target.value)}
        />

        <Button
          type="submit"
          disabled={ratingMutation.isPending || rating === 0 || isGuestMode}
          className="bg-primary"
        >
          {ratingMutation.isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              <span>Sending ...</span>
            </>
          ) : (
            "Send Feedback"
          )}
        </Button>
      </form>
    </WidgetCard>
  ) : null;
};

export default FeedbackWidget;
